<template>
  <div class="row">
    <div v-if="!isDashboard && [1, 5, 10].includes(role)" class="col-12 col-md-3">
      <consultant-list :role="role" @selected="viewCommissionData" />
    </div>

    <div class="col-12" :class="{'col-md-9': [1, 5, 10].includes(role)}">
      <commission-payment-settings-chart
        v-if="selectedUserId !== 0"
        :key="'chart-' + selectedUserId"
        :user-id="selectedUserId"
        :month="selectedMonth"
        :year="selectedYear"
        :position="selectedUserPosition"
      />

      <closed-students-navigation
        v-if="selectedUserId !== 0"
        ref="closedStudentsNavigation"
        :key="'nav-' + selectedUserId"
        :role="role"
        :user-id="selectedUserId"
        :user-name="selectedUserName"
        :position="selectedUserPosition"
        :month="selectedMonth"
        :year="selectedYear"
        @selected="viewCommissionData"
        @accepted="isCommissionsAccepted"
        @viewPendingApproval="showPendingApproval"
        @reload="getClosedStudents"
      />

      <closed-students-table
        v-if="selectedUserId !== 0"
        ref="closedStudentsTable"
        :key="'table-' + selectedUserId"
        :user-id="selectedUserId"
        :is-accepted="isAccepted"
        :month="selectedMonth"
        :year="selectedYear"
        @viewPrev="showPreviousCommissions"
        @viewEarned="showEarnedCommissions"
      />

      <commission-approval-modal
        v-if="!isAccepted && selectedUserId !== 0"
        ref="pendingApprovalModal"
        :user-id="selectedUserId"
        :messages="pendingApprovalMessages"
        :students="pendingApprovalStudents"
        :is-accepting-commission="isAcceptingCommission"
        @accept="acceptCommissions"
      />

      <earned-commissions-modal
        v-if="selectedUserId !== 0"
        ref="earnedCommissionsModal"
        :key="'earned-' + selectedUserId"
        :is-accepted="isAccepted"
        :month="selectedMonth"
        :year="selectedYear"
      />

      <previous-commissions-modal
        v-if="selectedUserId !== 0"
        ref="prevCommissionsModal"
        :key="'prev-' + selectedUserId"
      />
    </div>
  </div>
</template>

<script>
import { userPosition } from "../../services/api/commissions";
import ConsultantList from "./ConsultantList.vue";
import CommissionPaymentSettingsChart from "./components/CommissionPaymentSettingsChart.vue";
import ClosedStudentsNavigation from "./components/ClosedStudentsNavigation.vue";
import ClosedStudentsTable from "./components/ClosedStudentsTable.vue";
import EarnedCommissionsModal from "./components/EarnedCommissionsModal.vue";
import PreviousCommissionsModal from "./components/PreviousCommissionsModal.vue";
import CommissionApprovalModal from "./components/CommissionApprovalModal.vue";

export default {
  name: 'Commissions',
  components: {
    CommissionApprovalModal,
    CommissionPaymentSettingsChart,
    ClosedStudentsNavigation,
    ConsultantList,
    ClosedStudentsTable,
    EarnedCommissionsModal,
    PreviousCommissionsModal
  },
  props: {
    role: {
      type: Number,
      default: 0
    },
    userId: {
      type: Number,
      default: 0,
    },
    userName: {
      type: String,
      default: '',
    },
    position: {
      type: String,
      default: '',
    }
  },
  data() {
    return {
      isDashboard: false,
      isAccepted: false,
      isLoadingPosition: false,
      isAcceptingCommission: false,
      availablePeriods: [],
      availableCommissions: [],
      selectedUserId: this.userId,
      selectedUserName: this.userName,
      selectedMonth: new Date().getMonth() + 1,
      selectedYear: new Date().getFullYear(),
      selectedUserPosition: this.position,
      pendingApprovalMessages: [],
      pendingApprovalStudents: []
    }
  },
  created() {
    this.checkIsDashboard();
    this.parseHash();
  },
  methods: {
    checkIsDashboard() {
      this.isDashboard = window.location.pathname.includes('/admin/dashboard');
    },
    viewCommissionData(id, name, position, month, year) {
      this.selectedUserId = id;
      this.selectedUserName = name;
      this.selectedMonth = month;
      this.selectedYear = year;
      this.selectedUserPosition = position;
    },
    showEarnedCommissions(commissions) {
      this.$refs.earnedCommissionsModal.commissions = commissions;
      this.$refs.earnedCommissionsModal.showModal = true;
    },
    showPreviousCommissions(commissions) {
      this.$refs.prevCommissionsModal.commissions = commissions;
      this.$refs.prevCommissionsModal.showModal = true;
    },
    showPendingApproval(data, show) {
      this.$refs.pendingApprovalModal.showModal = show;
      this.pendingApprovalMessages = data.messages;
      this.pendingApprovalStudents = data.students;
    },
    isCommissionsAccepted(accepted) {
      this.isAccepted = !accepted;
    },
    async acceptCommissions(skip) {
      this.isAcceptingCommission = true;

      try {
        await this.$refs.closedStudentsNavigation.acceptCommissions(skip);
      } catch (error) {
        console.error('Error accepting commissions:', error);
      } finally {
        this.isAcceptingCommission = false;
      }
    },
    getClosedStudents(reload) {
      this.$refs.closedStudentsTable.fetchClosedStudents(reload);
    },
    async getSelectedUserPosition(userId) {
      try {
        this.isLoadingPosition = true;

        const response = await userPosition({user_id: userId});

        if (response.status === 200) {
          this.selectedUserPosition = response.data.position;
        }
      } catch (error) {
        console.error('Error fetching commission settings:', error);
      } finally {
        this.isLoadingPosition = false;
      }
    },
    parseHash() {
      const hash = window.location.hash.substring(1);
      const [userId, year, month] = hash.split('-');

      if (userId && year && month) {
        this.selectedUserPosition = '';
        this.selectedUserId = parseInt(userId, 10);
        this.selectedYear = parseInt(year, 10);
        this.selectedMonth = parseInt(month, 10);
        this.getSelectedUserPosition(userId);
      }
    }
  }
}
</script>